<template>
  <avs-card>
    <template v-slot:title>
      <v-card-title class="pl-0">{{ $t("Faire un Don") }}</v-card-title>
    </template>

    <template v-slot:content>
      <v-container>
        <v-form :readonly="!isEditable" ref="form">
          <v-select
            v-model="spending.recipient_company"
            :items="companies"
            :rules="rules.companies"
            item-text="name"
            item-value="pk"
            :label="$t('Entreprise')"
            :disabled="true"
            outlined dense
          />

          <v-row class="my-0">
            <v-col cols="6">
              <v-autocomplete
                v-model="spending.company_product"
                :disabled="!isEditable || products.length === 0"
                :items="products"
                :rules="rules.products"
                item-text="name" item-value="id"
                :label="$t('Produit')"
                attach dense outlined
                @change="onProductChange"
              />
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                v-model="spending.company_shop"
                :disabled="!isEditable || shops.length === 0"
                :items="shops"
                :rules="rules.shops"
                item-text="name" item-value="id"
                :label="$t('Site')"
                attach dense outlined
              />
            </v-col>
          </v-row>

          <v-row class="my-0">
            <v-col cols="6">
              <v-text-field
                v-model.number="spending.purchase_liters"
                :disabled="!isEditable"
                :rules="rules.purchase_liters"
                :label="$t('Nombre de litre')"
                outlined dense clearable
                @input="onLitersInput"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="spending.purchase_amount"
                :disabled="!isEditable"
                :label="$t('Montant Total')"
                prefix="XOF " outlined dense clearable
                @input="onAmountInput"
              />
            </v-col>
          </v-row>

          <v-row class="my-0">
            <v-col cols="6">
              <v-text-field
                v-model="spending.last_name"
                :disabled="!isEditable"
                :rules="rules.last_name"
                :label="$t('Nom du bénéficiaire')"
                dense outlined
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="spending.first_name"
                :disabled="!isEditable"
                :rules="rules.first_name"
                :label="$t('Prénom du bénéficiaire')"
                dense outlined
              />
            </v-col>
          </v-row>

          <v-text-field
            v-model="spending.phone_number"
            :disabled="!isEditable"
            :rules="rules.phone_number"
            @input="enforcePhoneFormat"
            :label="$t('Téléphone')"
            dense outlined
          />

          <!-- IMPORTANT : pas d’emit vers le parent ; v-model direct interne -->
          <DatetimePicker
            v-model="spending.valid_until"
            :disabled="!isEditable"
            :rules="rules.valid_until"
            :label="$t('Date d\'expiration')"
          />

          <v-col class="d-flex justify-end gap-4">
            <avs-saveButton v-if="!loading" @click="save" :disabled="!isEditable" />
            <avs-button v-else icon color="secondary" class="spin-loading"
              :iconI="loading ? 'mdi-loading spin' : ''" label fab small />
            <avs-cancelButton @click="cancel" />
          </v-col>
        </v-form>
      </v-container>
    </template>
  </avs-card>
</template>

<script>
import { mkDonation } from "@/utils/datamodels";
import { DonationDetailService, CompanyService } from "@/services";
import { required, max64, phone } from "@/utils/validator";
import { stringToDate, toISODatetime } from "@/utils/datetime";
import _ from "lodash";

export default {
  name: "DonationEditor",
  components: {
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
  },
  props: {
    hasUniqueName: { type: Function, required: true },
    isEditable: { type: Boolean, default: true },
    value: { type: Object, default: null }, // v-model (parent) — on n’émet plus pendant l’édition
  },
  data() {
    return {
      spending: mkDonation(),
      loading: false,
      products: [],
      shops: [],
      companies: [],
      product_price: 0,
      updatingField: null,
      initialized: false, // ← pour contrôler la (re)hydratation
      rules: {
        companies: [required],
        purchase_liters: [required, max64],
        valid_until: [required(this.$t("La date de validité est requise."))],
        last_name: [required],
        first_name: [required],
        phone_number: [required, phone],
        products: [required],
        shops: [],
      },
    };
  },
  created() {
    this.hydrateFromValue();
    this.initialized = true;
    this.fetchCompaniesThenSync();
  },
  watch: {
    // NE REHYDRATE QUE si on change d’item (id différent)
    value(newVal) {
      if (!this.initialized) return;
      const newId = newVal?.id ?? null;
      const curId = this.spending?.id ?? null;
      if (newId !== curId) {
        this.hydrateFromValue();
        if (this.companies?.length && this.spending?.recipient_company) {
          this.updateProductsAndShops(this.spending.recipient_company);
        }
      }
    },
  },
  methods: {
    hydrateFromValue() {
      // copie locale seule
      this.spending = this.value ? _.cloneDeep(this.value) : mkDonation();

      // s’assurer qu’on a l’id pour le PUT
      if (!this.spending.id && this.value?.id) this.spending.id = this.value.id;

      // défauts
      this.spending.purchase_liters ??= 0;
      this.spending.purchase_amount ??= 0;

      // normaliser la date pour le DatetimePicker (qui veut un objet date-like)
      const vu = this.spending.valid_until;
      if (vu) {
        if (typeof vu === "string") {
          const cleaned = vu.replace("T", " ").replace("Z", "");
          this.spending.valid_until = stringToDate(cleaned);
        }
        // si Date / moment / dayjs-like, on laisse tel quel (le picker sait gérer)
      }
    },

    async fetchCompaniesThenSync() {
      const companies = await CompanyService.getAll();
      this.companies = companies || [];

      const fallback = this.companies[0]?.pk;
      if (!this.spending.recipient_company && fallback) {
        this.spending.recipient_company = fallback;
      }
      if (this.spending.recipient_company) {
        this.updateProductsAndShops(this.spending.recipient_company);
      }
    },

    updateProductsAndShops(companyPk) {
      const selectedCompany = this.companies.find(c => c.pk === companyPk);
      if (!selectedCompany) {
        this.products = [];
        this.shops = [];
        this.product_price = 0;
        return;
      }
      this.products = (selectedCompany.products || []).map(p => ({ ...p, price: p.price }));
      this.shops = [{ id: "", name: "Tout le réseau" }, ...(selectedCompany.shops || [])];

      const selectedProduct = this.products.find(p => p.id === this.spending.company_product);
      this.product_price = selectedProduct ? Number(selectedProduct.price) || 0 : 0;

      this.calculateAmountFromLiters();
    },

    onProductChange(productId) {
      const product = this.products.find(p => p.id === productId);
      this.product_price = product ? Number(product.price) || 0 : 0;
      this.calculateAmountFromLiters();
    },

    calculateAmountFromLiters() {
      const liters = parseFloat(this.spending.purchase_liters) || 0;
      const price  = parseFloat(this.product_price) || 0;
      this.spending.purchase_amount = parseFloat((liters * price).toFixed(2));
    },

    onLitersInput() {
      if (this.updatingField === "amount") return;
      this.updatingField = "liters";
      this.calculateAmountFromLiters();
      this.$nextTick(() => (this.updatingField = null));
    },

    onAmountInput() {
      if (this.updatingField === "liters") return;
      this.updatingField = "amount";
      const amount = parseFloat(this.spending.purchase_amount) || 0;
      const price  = parseFloat(this.product_price) || 0;
      this.spending.purchase_liters = price > 0 ? parseFloat((amount / price).toFixed(2)) : 0;
      this.$nextTick(() => (this.updatingField = null));
    },

    enforcePhoneFormat() {
      if (!this.spending.phone_number?.startsWith("+225")) {
        this.spending.phone_number =
          "+225" + String(this.spending.phone_number || "").replace(/^(\+225)?/, "");
      }
    },

    cancel() {
      this.$emit("cancel");
    },

    async save() {
      this.loading = true;
      try {
        // préparer un payload “propre” (sans muter spending)
        const payload = {
          ...this.spending,
          first_name: String(this.spending.first_name || "").trim(),
          last_name:  String(this.spending.last_name  || "").trim(),
          phone_number: String(this.spending.phone_number || "").trim(),
          // convertir la date en ISO — ton util fait déjà la bonne conversion
          valid_until: this.spending.valid_until
            ? toISODatetime(this.spending.valid_until)
            : null,
          lang: this.$i18n.locale,
        };

        // (debug) vérifie en console que les NOUVELLES valeurs partent bien
        // console.log("PAYLOAD SENT:", payload);

        if (!this.$refs.form.validate()) { this.loading = false; return; }

        const response = payload?.id
          ? await DonationDetailService.put(payload)   // UPDATE
          : await DonationDetailService.post(payload); // CREATE

        // on ne remonte rien tant que le parent n’a pas accusé succès
        this.$emit("input", response);
        this.$emit("success");
      } catch (error) {
        const message = error?.external_emails || "Erreur lors de l'enregistrement";
        this.$emit("error", message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.spin-loading { animation: rotation 1s linear infinite; }
@keyframes rotation { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
